import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { InfoFooter } from "./InfoPages";

// ==========================================================================
// RAW COMPANY — 60-second cinematic brand reel
// Choreographed across a fixed 60s timeline. Each "scene" shows/hides based
// on `t` (seconds elapsed). Animations are pure CSS — no external deps.
// Screen-record it on your phone (iOS: swipe down from top-right, red dot)
// to produce an MP4 you can post straight to Instagram / TikTok.
// ==========================================================================
const DURATION = 60;

const Reel = () => {
  const [t, setT] = useState(0);
  const [running, setRunning] = useState(false);
  const [products, setProducts] = useState([]);
  const [hideHint, setHideHint] = useState(false);
  const startedAt = useRef(0);
  const rafId = useRef(null);

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts((r.data || []).filter((p) => p.published).slice(0, 6));
    }).catch(() => {});
  }, []);

  const tick = () => {
    const elapsed = (performance.now() - startedAt.current) / 1000;
    if (elapsed >= DURATION) {
      setT(DURATION);
      setRunning(false);
      return;
    }
    setT(elapsed);
    rafId.current = requestAnimationFrame(tick);
  };

  const play = () => {
    setHideHint(true);
    setRunning(true);
    startedAt.current = performance.now();
    rafId.current = requestAnimationFrame(tick);
  };
  const reset = () => {
    cancelAnimationFrame(rafId.current);
    setT(0);
    setRunning(false);
  };

  useEffect(() => () => cancelAnimationFrame(rafId.current), []);

  // Scene windows (seconds)
  const inRange = (from, to) => t >= from && t < to;
  const pct = (from, to) => Math.max(0, Math.min(1, (t - from) / (to - from)));

  // Which product is on screen during the 24s showcase window (28s → 52s)
  const showcaseStart = 28;
  const showcasePer = 4;
  const productIndex = Math.min(
    products.length - 1,
    Math.max(0, Math.floor((t - showcaseStart) / showcasePer))
  );
  const currentProduct = products[productIndex];

  return (
    <div
      data-testid="reel-stage"
      className="fixed inset-0 overflow-hidden bg-black text-white"
      style={{
        backgroundImage: "url('/bg-texture.jpeg')",
        backgroundRepeat: "repeat",
        backgroundSize: "340px 340px",
      }}
    >
      {/* Deepen vignette for cinematic look */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.85) 100%)" }}
      />

      {/* Soft gold tailor-mark watermark behind everything */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "url(/raw-lion-watermark.png?v=4)",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          backgroundSize: "min(75vw, 620px) auto",
          opacity: 0.08,
          mixBlendMode: "screen",
        }}
      />

      {/* ===================== SCENE 1: LOGO REVEAL (0 → 6s) ===================== */}
      {inRange(0, 8) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div
              className="mx-auto w-40 h-40 sm:w-56 sm:h-56 border-2 border-[#d4af37] bg-black overflow-hidden shadow-[0_0_60px_rgba(212,175,55,0.5)]"
              style={{
                transform: `scale(${0.4 + pct(0, 3) * 0.6}) rotate(${-180 + pct(0, 3) * 180}deg)`,
                opacity: pct(0, 1.5),
                transition: "none",
              }}
            >
              <img src="/raw-company-logo.jpeg" alt="" className="w-full h-full object-contain" />
            </div>
            <div
              className="mt-6 sm:mt-10 font-display text-[#d4af37]"
              style={{
                opacity: Math.max(0, pct(3, 4.5) - pct(7, 8) * 2),
                transform: `translateY(${(1 - pct(3, 4.5)) * 20}px)`,
              }}
            >
              <div className="text-[10px] sm:text-xs tracking-[0.5em] text-[#d4af37]/70 mb-3">EST. 2026</div>
              <div className="text-4xl sm:text-6xl tracking-tighter">RAW COMPANY</div>
              <div className="text-[10px] sm:text-sm tracking-[0.45em] mt-2 text-[#d4af37]/80">COUTURE ATELIER</div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== SCENE 2: TAGLINE (8 → 14s) ===================== */}
      {inRange(8, 14) && (
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="text-center">
            <div
              className="text-[10px] sm:text-xs tracking-[0.5em] text-[#d4af37] mb-6"
              style={{ opacity: pct(8, 9.5) }}
            >
              — BRITISH STREETWEAR —
            </div>
            <div
              className="font-display text-3xl sm:text-6xl leading-[1.05] text-white"
              style={{
                opacity: Math.max(0, pct(8.5, 10) - pct(13, 14)),
                transform: `translateY(${(1 - pct(8.5, 10)) * 30}px)`,
              }}
            >
              ENGINEERED<br />
              <span className="text-[#d4af37]">GARMENTS.</span>
            </div>
            <div
              className="mt-5 font-mono text-[11px] sm:text-sm tracking-[0.3em] text-zinc-400"
              style={{ opacity: Math.max(0, pct(10, 11.5) - pct(13, 14)) }}
            >
              FACTORY-SPEC TRANSPARENCY
            </div>
          </div>
        </div>
      )}

      {/* ===================== SCENE 3: FEATURE 1 — AI DESIGN (14 → 21s) ===================== */}
      {inRange(14, 21) && (
        <FeatureCard
          t={t} start={14} end={21}
          kicker="// 01 · DESIGN"
          title="AI-CRAFTED"
          sub="Every piece rendered in-house"
          body="From concept sketch to production-ready tech-pack — powered by state-of-the-art AI visual rendering."
        />
      )}

      {/* ===================== SCENE 4: FEATURE 2 — FACTORY-DIRECT (21 → 28s) ===================== */}
      {inRange(21, 28) && (
        <FeatureCard
          t={t} start={21} end={28}
          kicker="// 02 · PRODUCTION"
          title="FACTORY-DIRECT"
          sub="No middlemen. No markup."
          body="Embroidered branding. Stitched construction. Full tech-packs delivered straight to premium UK manufacturers."
        />
      )}

      {/* ===================== SCENE 5: PRODUCT SHOWCASE (28 → 52s) ===================== */}
      {inRange(28, 52) && (
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="text-center w-full max-w-md">
            <div
              className="text-[9px] sm:text-[10px] tracking-[0.5em] text-[#d4af37] mb-6"
              style={{ opacity: pct(28, 29.5) }}
            >
              — THE COLLECTION —
            </div>
            {currentProduct ? (
              <div
                key={productIndex}
                className="relative"
                style={{
                  animation: "reelFadeIn 0.6s cubic-bezier(0.2, 0.7, 0.2, 1)",
                }}
              >
                <div className="aspect-[4/5] border border-[#d4af37]/50 bg-black/60 backdrop-blur-sm mx-auto overflow-hidden relative"
                  style={{
                    maxWidth: "min(320px, 75vw)",
                    boxShadow: "0 0 80px rgba(212,175,55,0.35)",
                  }}
                >
                  {currentProduct.image_base64 && (
                    <img
                      src={
                        currentProduct.image_base64.startsWith("data:")
                          ? currentProduct.image_base64
                          : `data:image/png;base64,${currentProduct.image_base64}`
                      }
                      alt={currentProduct.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ animation: "reelZoom 4s ease-out" }}
                    />
                  )}
                  <div className="absolute top-3 right-3 border border-[#d4af37] px-2 py-0.5 text-[9px] tracking-[0.3em] text-[#d4af37] font-mono uppercase bg-black/60">
                    {currentProduct.clothing_type}
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black to-transparent">
                    <div className="text-white font-display text-lg tracking-tight">
                      {currentProduct.name}
                    </div>
                    <div className="text-[#d4af37] font-mono text-sm mt-1">
                      £{Number(currentProduct.price).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-zinc-500 text-sm font-mono">// no products yet</div>
            )}
          </div>
        </div>
      )}

      {/* ===================== SCENE 6: CALL TO ACTION (52 → 60s) ===================== */}
      {inRange(52, 60) && (
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="text-center">
            <div
              className="font-display text-[#d4af37]"
              style={{
                transform: `scale(${0.7 + pct(52, 54) * 0.3})`,
                opacity: pct(52, 53.5),
              }}
            >
              <div className="text-[10px] sm:text-xs tracking-[0.5em] mb-4 text-[#d4af37]/70">SHOP THE DROP</div>
              <div className="text-4xl sm:text-7xl tracking-tighter mb-6">RAW COMPANY</div>
            </div>
            <div
              className="font-mono tracking-[0.25em] text-white text-sm sm:text-base"
              style={{
                opacity: Math.max(0, pct(54, 55.5) - pct(59, 60)),
                transform: `translateY(${(1 - pct(54, 55.5)) * 20}px)`,
              }}
            >
              RAWCOMPANYCLOTHING.COM
            </div>
            <div
              className="mt-8 inline-block border-2 border-[#d4af37] px-8 py-3 text-[#d4af37] font-mono tracking-[0.3em] text-xs"
              style={{
                opacity: Math.max(0, pct(55.5, 57) - pct(59, 60)),
                boxShadow: "0 0 30px rgba(212,175,55,0.35)",
              }}
            >
              → WEAR RAW
            </div>
          </div>
        </div>
      )}

      {/* ======================== HUD (only before play) ======================== */}
      {!running && !hideHint && (
        <div
          data-testid="reel-hint"
          className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50"
        >
          <div className="max-w-md w-full text-center px-6">
            <div className="text-[#d4af37] text-xs tracking-[0.5em] mb-6">// 60-SECOND BRAND REEL</div>
            <div className="font-display text-3xl sm:text-5xl mb-6">READY TO RECORD</div>
            <ol className="text-left text-zinc-300 text-sm font-mono space-y-2 mb-8">
              <li>1. Turn your phone on its side for a wider shot (optional).</li>
              <li>2. iPhone: swipe down from top-right — tap the red RECORD dot.</li>
              <li>3. Android: pull down the quick-settings — tap SCREEN RECORD.</li>
              <li>4. Come back here, tap PLAY, leave it until the end.</li>
              <li>5. Stop recording — the MP4 saves to your camera roll.</li>
            </ol>
            <button
              data-testid="reel-play-btn"
              onClick={play}
              className="w-full bg-[#d4af37] text-black font-bold tracking-[0.3em] py-4 text-sm hover:shadow-[0_0_30px_rgba(212,175,55,0.8)] transition-shadow"
            >
              ▶ PLAY 60-SECOND REEL
            </button>
            <div className="mt-5 flex justify-center gap-4">
              <Link to="/" data-testid="reel-exit" className="text-zinc-500 hover:text-[#d4af37] text-[10px] tracking-[0.3em] uppercase">← Back to shop</Link>
            </div>
          </div>
        </div>
      )}

      {/* Post-play controls */}
      {!running && hideHint && (
        <div className="absolute bottom-6 inset-x-0 flex justify-center gap-3 z-40">
          <button
            data-testid="reel-replay"
            onClick={play}
            className="btn-raw btn-raw-primary"
          >
            ↻ REPLAY
          </button>
          <button
            data-testid="reel-reset"
            onClick={reset}
            className="btn-raw"
          >
            ← BACK
          </button>
        </div>
      )}

      {/* Tiny progress bar */}
      {running && (
        <div className="absolute top-0 left-0 h-1 bg-[#d4af37]" style={{ width: `${(t / DURATION) * 100}%`, transition: "width 0.1s linear" }} />
      )}

      {/* Keyframes */}
      <style>{`
        @keyframes reelFadeIn {
          from { opacity: 0; transform: scale(0.92) translateY(10px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes reelZoom {
          from { transform: scale(1.0); }
          to   { transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
};

// Reusable feature-card scene
const FeatureCard = ({ t, start, end, kicker, title, sub, body }) => {
  const p = Math.max(0, Math.min(1, (t - start) / (end - start)));
  const enter = Math.max(0, Math.min(1, (t - start) / 1.2));
  const exit = Math.max(0, Math.min(1, (end - t) / 1));
  const alpha = Math.min(enter, exit);

  return (
    <div className="absolute inset-0 flex items-center justify-center px-6">
      <div className="max-w-lg w-full" style={{ opacity: alpha }}>
        <div
          className="text-[9px] sm:text-[10px] tracking-[0.5em] text-[#d4af37] mb-3"
          style={{ transform: `translateX(${(1 - enter) * -30}px)` }}
        >
          {kicker}
        </div>
        <div
          className="font-display text-4xl sm:text-7xl tracking-tighter text-white leading-[0.95]"
          style={{ transform: `translateX(${(1 - enter) * -40}px)` }}
        >
          {title}
        </div>
        <div
          className="mt-2 font-display text-xl sm:text-3xl text-[#d4af37]/80 italic"
          style={{ transform: `translateX(${(1 - enter) * -30}px)` }}
        >
          {sub}
        </div>
        <div
          className="mt-5 text-zinc-300 text-sm sm:text-base leading-relaxed max-w-md"
          style={{ opacity: p > 0.25 ? alpha : 0 }}
        >
          {body}
        </div>
      </div>
      <InfoFooter />
    </div>
  );
};

export default Reel;
